// ============================================================
// SidebarFlyout.tsx — the COLLAPSED rail's Favourites / Recent cases
// (option A, design/sidebar-icon-mockups). Collapsed, the rail can't show
// case names, so each section is ONE icon (a star / a history clock, the
// same 20px stroke-2 glyph weight as every nav icon) with a count badge;
// hover (short delay) or click opens a small named flyout beside it:
// "client v defendant · case ref" rows, click → the case.
//
// Keyboard: the icon is a real button (Enter / Space opens and moves focus
// to the first row); ↑ / ↓ (and Home / End) move through the rows, Enter
// opens one, Esc closes and returns focus to the icon. Tab from a row moves to
// "Expand sidebar ›"; Tab from there (or Shift+Tab out) closes and carries on
// to the NEXT / previous control in the sidebar (the list is portalled to the
// end of <body>, so the browser's own Tab order would leave the page).
// Opening a row closes the list and puts focus back on its icon.
// Touch: click opens (no hover dependency). Outside click closes.
// The flyout is portalled with position:fixed (the rail's scroll region
// would clip an absolute child) and clamped inside the viewport — near the
// bottom it moves up, and its arrow stays on the icon.
// Styling is the RailTooltip family: forest panel, 1px white/10 ring,
// shadow, small arrow back to the icon.
// ============================================================
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export type FlyoutItemKind = 'case' | 'client' | 'task'
export interface FlyoutItem {
  key: string
  kind: FlyoutItemKind
  name: string
  sub: string
  onOpen: () => void
}

const OPEN_DELAY_MS = 200
const CLOSE_GRACE_MS = 180
const MARGIN = 8 // keep this far from the viewport edges
const ARROW_FROM_TOP = 22 // the arrow's resting offset (aligned to the header row)

const svgProps = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true }
export const StarGlyph: React.FC<{ className?: string }> = ({ className = 'h-5 w-5 shrink-0' }) => (
  <svg className={className} {...svgProps}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
)
export const HistoryGlyph: React.FC<{ className?: string }> = ({ className = 'h-5 w-5 shrink-0' }) => (
  <svg className={className} {...svgProps}><path d="M3 3v5h5" /><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" /><path d="M12 7v5l4 2" /></svg>
)
const RowGlyph: React.FC<{ kind: FlyoutItemKind }> = ({ kind }) => (
  <svg className="h-4 w-4 shrink-0 text-casevo-mid" {...svgProps}>
    {kind === 'client'
      ? <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>
      : kind === 'task'
        ? <><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>
        : <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />}
  </svg>
)

interface Props {
  section: 'favourites' | 'recent'
  title: string // "Favourites" / "Recent cases"
  items: FlyoutItem[] | null // null = still loading
  emptyText: string
  onExpandSidebar?: () => void
}

// Focus the next (dir 1) / previous (dir -1) tabbable control after `from` in
// the page's order — how Tab would move had the list not been portalled.
const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
export const focusBeside = (from: HTMLElement | null, dir: 1 | -1): void => {
  if (!from) return
  const all = Array.from(document.querySelectorAll<HTMLElement>(TABBABLE))
    .filter((el) => el.tabIndex >= 0 && !el.closest('[role="menu"], [hidden], [aria-hidden="true"]'))
  const i = all.indexOf(from)
  if (i < 0) { from.focus(); return }
  ;(all[i + dir] ?? from).focus()
}

const SidebarFlyout: React.FC<Props> = ({ section, title, items, emptyText, onExpandSidebar }) => {
  const [open, setOpen] = useState(false)
  // A click (or keyboard) open is "pinned": leaving with the mouse doesn't close it.
  const pinned = useRef(false)
  const openTimer = useRef<number | undefined>(undefined)
  const closeTimer = useRef<number | undefined>(undefined)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const focusFirstOnOpen = useRef(false)
  const [pos, setPos] = useState<{ left: number; top: number; arrow: number } | null>(null)
  const count = items?.length ?? 0
  const panelId = `sidebar-flyout-${section}`

  const clearTimers = (): void => { window.clearTimeout(openTimer.current); window.clearTimeout(closeTimer.current) }
  const expandRef = useRef<HTMLButtonElement | null>(null)
  const close = useCallback((returnFocus = false): void => {
    clearTimers()
    pinned.current = false
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }, [])
  const openNow = (pin: boolean, focusFirst = false): void => {
    clearTimers()
    if (pin) pinned.current = true
    focusFirstOnOpen.current = focusFirst
    setOpen(true)
  }

  // Place beside the icon, clamped into the viewport; the arrow follows the icon.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return }
    const t = triggerRef.current
    const p = panelRef.current
    if (!t) return
    const r = t.getBoundingClientRect()
    const h = p?.offsetHeight ?? 0
    const mid = r.top + r.height / 2
    let top = mid - ARROW_FROM_TOP - 4
    const maxTop = window.innerHeight - MARGIN - h
    if (top > maxTop) top = maxTop
    if (top < MARGIN) top = MARGIN
    const arrow = Math.min(Math.max(mid - top - 4, 12), Math.max(12, h - 20))
    // 8px clear of the RAIL's edge (not the icon's), so the arrow never sits on the rail.
    const railRight = t.closest('aside')?.getBoundingClientRect().right ?? r.right + 8
    setPos({ left: railRight + 8, top, arrow })
  }, [open, count])

  // Focus the first row when opened from the keyboard.
  useEffect(() => {
    if (open && pos && focusFirstOnOpen.current) {
      focusFirstOnOpen.current = false
      itemRefs.current[0]?.focus()
    }
  }, [open, pos])

  // Outside click closes.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      const tgt = e.target as Node
      if (triggerRef.current?.contains(tgt) || panelRef.current?.contains(tgt)) return
      close()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, close])

  useEffect(() => () => clearTimers(), [])

  // Hover: open after a short delay; leaving the icon AND the panel closes
  // after a short grace (unless pinned by a click / keyboard).
  const onEnter = (): void => {
    window.clearTimeout(closeTimer.current)
    if (open) return
    openTimer.current = window.setTimeout(() => openNow(false), OPEN_DELAY_MS)
  }
  const onLeave = (): void => {
    window.clearTimeout(openTimer.current)
    if (pinned.current) return
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_GRACE_MS)
  }

  const onTriggerClick = (e: React.MouseEvent): void => {
    // detail === 0 → keyboard activation (Enter / Space): move into the list.
    const fromKeyboard = e.detail === 0
    if (open && pinned.current) { close(fromKeyboard); return }
    openNow(true, fromKeyboard)
  }
  const onTriggerKey = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault()
      if (!open) openNow(true, true)
      else itemRefs.current[0]?.focus()
    } else if (e.key === 'Escape' && open) {
      e.preventDefault()
      close(true)
    }
  }
  const onListKey = (e: React.KeyboardEvent): void => {
    const rows = itemRefs.current.filter((x): x is HTMLButtonElement => !!x)
    const i = rows.indexOf(document.activeElement as HTMLButtonElement)
    if (e.key === 'ArrowDown') { e.preventDefault(); rows[(i + 1) % rows.length]?.focus() }
    else if (e.key === 'ArrowUp') { e.preventDefault(); rows[(i - 1 + rows.length) % rows.length]?.focus() }
    else if (e.key === 'Home') { e.preventDefault(); rows[0]?.focus() }
    else if (e.key === 'End') { e.preventDefault(); rows[rows.length - 1]?.focus() }
    else if (e.key === 'Escape') { e.preventDefault(); close(true) }
    else if (e.key === 'Tab') {
      e.preventDefault()
      const onExpand = document.activeElement === expandRef.current
      // Forward from a row → the "Expand sidebar ›" link (when there is one).
      if (!e.shiftKey && !onExpand && expandRef.current) { expandRef.current.focus(); return }
      // Otherwise leave the list: close, and move on from the ICON in the page's order.
      close()
      focusBeside(triggerRef.current, e.shiftKey ? -1 : 1)
    }
  }
  // Open a case: close and put focus back on the icon (a sensible, visible
  // place to continue from), then navigate.
  const openItem = (it: FlyoutItem): void => { close(true); it.onOpen() }

  const isFav = section === 'favourites'
  const Glyph = isFav ? StarGlyph : HistoryGlyph
  const badge = count > 99 ? '99+' : String(count)

  return (
    <div className="hidden md:flex items-center justify-center w-full h-9" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button
        ref={triggerRef}
        type="button"
        onClick={onTriggerClick}
        onKeyDown={onTriggerKey}
        aria-label={`${title}${items ? ` (${count})` : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className={`relative flex items-center justify-center w-[calc(100%-16px)] h-8 rounded-[10px] border transition-[background-color,border-color,color,transform] duration-100
                    focus:outline-none focus-visible:border-[rgba(255,255,255,0.35)] active:scale-95
                    ${open
                      ? 'bg-[rgba(255,255,255,0.08)] border-[rgba(255,255,255,0.12)] text-white'
                      : 'border-transparent text-casevo-light hover:bg-[rgba(255,255,255,0.04)] hover:border-[rgba(255,255,255,0.05)] hover:text-white'}`}
      >
        <span className="relative inline-flex">
          <Glyph />
          {count > 0 && (
            <span
              aria-hidden="true"
              data-testid={`${section}-badge`}
              className={`absolute -top-2 -right-3 min-w-[16px] h-4 px-1 rounded-full border-2 border-casevo-header grid place-items-center
                          text-[10px] font-bold leading-[12px] tabular-nums
                          ${isFav ? 'bg-casevo-gold text-casevo-on-gold' : 'bg-casevo-light text-casevo-on-light'}`}
            >
              {badge}
            </span>
          )}
        </span>
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          id={panelId}
          role="menu"
          aria-label={title}
          onKeyDown={onListKey}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          style={{ left: pos?.left ?? -9999, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden' }}
          className="fixed z-[300] w-[296px] max-h-[calc(100vh-16px)] flex flex-col font-sans rounded-[10px] bg-casevo-tooltip py-1.5 text-casevo-light
                     ring-1 ring-white/10 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.30),0_4px_6px_-4px_rgba(0,0,0,0.25)]"
        >
          {/* Arrow back to the icon (follows the icon when the panel is clamped). */}
          <span aria-hidden="true" style={{ top: pos?.arrow ?? ARROW_FROM_TOP }}
            className="absolute -left-1 w-2 h-2 rotate-45 bg-casevo-tooltip shadow-[-1px_1px_0_rgba(255,255,255,0.10)]" />
          <div className="flex items-center gap-2 px-3.5 pt-2 pb-1.5 text-xs leading-4 uppercase tracking-widest text-casevo-mid shrink-0">
            <Glyph className={`h-[13px] w-[13px] shrink-0 ${isFav ? 'text-casevo-gold' : ''}`} />
            {title}
            {count > 0 && <span className="ml-auto text-[11px] tracking-normal text-casevo-light tabular-nums">{count}</span>}
          </div>
          <div className="overflow-y-auto min-h-0">
            {items === null ? (
              <div className="px-3.5 py-2 text-[12.5px] text-casevo-mid">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-3.5 py-2 text-[12.5px] text-casevo-mid">{emptyText}</div>
            ) : items.map((it, i) => (
              <button
                key={it.key}
                ref={(el) => { itemRefs.current[i] = el }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                onClick={() => openItem(it)}
                className="group/fi flex items-center gap-2.5 mx-1.5 my-px w-[calc(100%-12px)] px-2 py-1.5 rounded-lg border border-transparent text-left
                           hover:bg-white/[0.06] hover:border-white/[0.08] focus:outline-none focus-visible:bg-white/[0.06] focus-visible:border-white/[0.25]"
              >
                <RowGlyph kind={it.kind} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] leading-[18px] text-casevo-light truncate group-hover/fi:text-white group-focus-visible/fi:text-white">{it.name}</span>
                  <span className="block text-[11.5px] leading-[15px] text-casevo-mid truncate tracking-[0.03em]">{it.sub}</span>
                </span>
              </button>
            ))}
          </div>
          {onExpandSidebar && (
            <div className="mt-1.5 border-t border-white/[0.08] px-3.5 pt-2 pb-1 flex justify-between gap-2.5 text-xs text-casevo-mid shrink-0">
              <span>{items && items.length > 0 ? `Click ${isFav ? 'an item' : 'a case'} to open it` : ' '}</span>
              <button type="button" ref={expandRef} onClick={() => { close(); onExpandSidebar() }}
                className="font-semibold text-casevo-light hover:text-white rounded-[4px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
                Expand sidebar ›
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}

export default SidebarFlyout
